import { translate, Translation } from '@teamleader/i18n';
import { AnimationItem } from 'lottie-web';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { mutate } from 'swr';

import { useIsExtraLarge, useIsHuge, useIsSmall } from '../../../../../Components/Context/Hooks/mediaQueries';
import useLogoPosition from '../../../../../Components/Context/Hooks/useLogoPosition';
import Box from '../../../../../Components/UI/Box';
import Branding from '../../../../../Components/UI/Branding';
import Button from '../../../../../Components/UI/Button';
import Checkbox from '../../../../../Components/UI/Checkbox';
import FormItem from '../../../../../Components/UI/FormItem';
import FormLabel from '../../../../../Components/UI/FormLabel';
import Input from '../../../../../Components/UI/Input';
import Link from '../../../../../Components/UI/Link';
import Lottie from '../../../../../Components/UI/Lottie';
import MobilePopup from '../../../../../Components/UI/MobilePopup';
import OverflowingFlexBox from '../../../../../Components/UI/OverflowingFlexBox';
import Sidebar, { SIZES } from '../../../../../Components/UI/Sidebar';
import Textarea from '../../../../../Components/UI/Textarea';
import { WithTooltip } from '../../../../../Components/UI/Tooltip';
import Plane from '../../../../../Svg/120x120_plane.svg';
import Send from '../../../../../Svg/120x120_send.svg';
import sending from '../../../../../Svg/Lottie/sending.json';
import { EVENTS, track } from '../../../../../Utils/analytics';
import { canOpenNavigationForDeal } from '../../../../../Utils/canOpenNavigation';
import { MODES, UrlContext } from '../../../../../Utils/jwt';
import { captureException } from '../../../../../Utils/sentry';
import useGlobalContext, { DealContext } from '../../../Context';
import ErrorMessage from '../../ErrorMessage';
import SuccessMessage from '../../SuccessMessage';
import * as styles from '../../styles.css';

interface FeedbackFormData {
  subject?: string;
  content?: string;
  sendCopy?: boolean;
}

enum SENDING_STATE {
  IDLE = 'idle',
  PROCESSING = 'processing',
  SUCCESS = 'success',
  FAILURE = 'failure',
}

interface Props {
  isRequestNewQuotation?: boolean;
  isRefuseQuotation?: boolean;
}

const ButtonWithTooltip = WithTooltip(Button);

const FeedbackMenu: React.FC<Props> = ({ isRequestNewQuotation = false, isRefuseQuotation = false }) => {
  const [sendingState, setSendingState] = useState(SENDING_STATE.IDLE);
  const [isCopySent, setIsCopySent] = useState(false);
  const { quotation, deal } = useContext(DealContext);
  const { handleCloseMenu, request, type, openMenu } = useGlobalContext();
  const { mode } = useContext(UrlContext);
  const { inSuccessMessage } = useLogoPosition(canOpenNavigationForDeal(deal));
  const subjectChanged = useRef(false);
  const contentChanged = useRef(false);
  const isSmall = useIsSmall();
  const isExtraLarge = useIsExtraLarge();
  const isHuge = useIsHuge();
  const Element = isSmall ? MobilePopup : Sidebar;
  const animation = useRef<AnimationItem>(null);

  const email = deal!.responsibleUser ? deal!.responsibleUser.email : deal!.department.email;

  useEffect(() => setSendingState(SENDING_STATE.IDLE), [openMenu?.time]);

  const handleSubmit = useCallback(
    (formValues: FeedbackFormData) => {
      (async () => {
        setSendingState(SENDING_STATE.PROCESSING);
        try {
          if (isRequestNewQuotation) {
            await request!('/quotations.requestExtension', {
              body: JSON.stringify({
                id: quotation!.id,
                send_to: email,
                send_copy_to: formValues.sendCopy ? deal!.client.email : null,
                subject: formValues.subject,
                content: formValues.content,
              }),
            });
          } else if (isRefuseQuotation) {
            const feedback = [formValues.subject];
            if (formValues.content?.length) {
              feedback.push(formValues.content);
            }
            await request!('/quotations.refuse', {
              body: JSON.stringify({
                id: quotation!.id,
                feedback: feedback.join(' - '),
              }),
            });
          } else {
            await request!('/deals.sendFeedback', {
              body: JSON.stringify({
                id: quotation!.id,
                to: email,
                subject: formValues.subject,
                content: formValues.content,
                send_copy_to: formValues.sendCopy ? deal!.client.email : null,
              }),
            });
          }
        } catch (error) {
          setSendingState(SENDING_STATE.FAILURE);
          captureException(error);
          return;
        }

        await new Promise<void>((resolve) => {
          if (!animation.current) {
            resolve();
          }

          animation.current!.addEventListener('loopComplete', () => resolve());
        });
        animation.current?.removeEventListener('loopComplete');
        if (isRefuseQuotation) {
          await mutate('/deals.info');
        }

        setSendingState(SENDING_STATE.SUCCESS);
        setIsCopySent(!!formValues.sendCopy);
        if (!isRequestNewQuotation) {
          track(EVENTS.SENT_FEEDBACK, {
            email: formValues.sendCopy ? 'optIn' : 'optOut',
            quotation: quotation!.id,
          });
        }
      })();
    },
    [setIsCopySent, deal, email, quotation, request, isRequestNewQuotation, isRefuseQuotation],
  );

  const handleValidate = useCallback(
    ({ subject, content }: FeedbackFormData) => {
      const errors: Record<string, boolean> = {};

      if (!subject || subject.trim() === '') {
        errors.subject = true;
      }

      if (!isRefuseQuotation && (!content || content.trim() === '')) {
        errors.content = true;
      }

      return errors;
    },
    [isRefuseQuotation],
  );

  const handleChangeSubject = useCallback((onChange: (...args: unknown[]) => void, ...args: unknown[]) => {
    onChange(...args);

    if (!subjectChanged.current) {
      subjectChanged.current = true;
      track(EVENTS.CHANGED_FEEDBACK_SUBJECT);
    }
  }, []);

  const handleChangedContent = useCallback((onChange: (...args: unknown[]) => void, ...args: unknown[]) => {
    onChange(...args);

    if (!contentChanged.current) {
      contentChanged.current = true;
      track(EVENTS.CHANGED_FEEDBACK_CONTENT);
    }
  }, []);

  const handleChangeSendCopy = useCallback(
    (onChange: (...args: any[]) => void, checked: boolean, event: React.SyntheticEvent) => {
      onChange(checked, event);
      checked ? track(EVENTS.SELECTED_EMAIL_OPTIN) : track(EVENTS.SELECTED_EMAIL_OPTOUT);
    },
    [],
  );

  const handleCloseFeedbackMenu = useCallback(() => {
    if (sendingState === SENDING_STATE.IDLE) {
      track(EVENTS.CANCELLED_ADDING_FEEDBACK);
    }

    handleCloseMenu!();
  }, [handleCloseMenu, sendingState]);

  // We cannot have newlines in translations, since OneSky returns it as array,
  // which is not allowed by @teamleader/i18n package, so this is workaround
  const requestNewQuotationContent = [
    translate('expiredQuotation.requestNewContent.greeting'),
    translate('expiredQuotation.requestNewContent.question'),
    translate('expiredQuotation.requestNewContent.salutation'),
    '',
  ].join('\n\n');
  const initialValues = isRequestNewQuotation
    ? {
        subject: translate('expiredQuotation.requestNewSubject'),
        content: requestNewQuotationContent,
      }
    : isRefuseQuotation
    ? {
        subject: translate('feedbackSidebar.subject.refusePlaceholder', {
          quotationName: quotation!.name,
        }),
        content: undefined,
      }
    : {
        subject: translate('feedbackSidebar.subject.placeholder', {
          quotationName: quotation!.name,
        }),
        content: undefined,
      };
  return (
    <Form
      key={`${quotation!.id} ${openMenu ? openMenu.time.toString() : ''}`}
      onSubmit={handleSubmit}
      validate={handleValidate}
      render={({ handleSubmit, invalid }) => (
        <Element
          className={styles['menu']}
          display="flex"
          flexDirection="column"
          element="form"
          onSubmit={handleSubmit}
          onClose={sendingState !== SENDING_STATE.PROCESSING ? handleCloseFeedbackMenu : undefined}
          size={isHuge ? SIZES.WIDEST : isExtraLarge ? SIZES.WIDER : undefined}
        >
          {sendingState === SENDING_STATE.SUCCESS && (
            <SuccessMessage
              icon={<Send />}
              title={translate('feedbackSidebar.sentMessageTitle')}
              description={
                <Translation
                  id={isCopySent ? 'feedbackSidebar.sentMessageText.withCopy' : 'feedbackSidebar.sentMessageText'}
                  values={{
                    email: (): React.ReactNode => <Link href={`mailto:${email}`}>{email}</Link>,
                  }}
                />
              }
              branding={
                inSuccessMessage ? (
                  <Branding language={deal?.client.language} country={deal?.client.country} type={type} />
                ) : undefined
              }
            />
          )}
          {sendingState === SENDING_STATE.FAILURE && (
            <ErrorMessage
              icon={<Send />}
              title={translate('feedbackSidebar.errorMessageTitle')}
              description={translate('feedbackSidebar.errorMessageText')}
            />
          )}
          <Box
            flex={1}
            flexDirection="column"
            alignItems="center"
            className={styles['message']}
            paddingHorizontal={4}
            display={sendingState === SENDING_STATE.PROCESSING ? 'flex' : 'none'}
          >
            <Lottie
              ref={animation}
              animationData={sending}
              loop
              play={sendingState === SENDING_STATE.PROCESSING}
              className={styles['message-icon']}
            >
              <Plane />
            </Lottie>
            <h2 className={styles['message-title']}>{translate('feedbackSidebar.sendingMessageTitle')}</h2>
            <p className={styles['message-description']}>{translate('feedbackSidebar.sendingMessageText')}</p>
          </Box>
          {sendingState === SENDING_STATE.IDLE && (
            <>
              <OverflowingFlexBox>
                <Box paddingBottom={4} paddingHorizontal={4} className={styles['container']}>
                  <FormItem label={<FormLabel>{translate('feedbackSidebar.to')}</FormLabel>}>
                    <p className={styles['email']}>{email}</p>
                  </FormItem>
                  <FormItem label={<FormLabel>{translate('feedbackSidebar.subject')}</FormLabel>}>
                    <Field
                      name="subject"
                      initialValue={initialValues.subject}
                      render={({ input, meta }): React.ReactNode => (
                        <Input
                          {...input}
                          hasError={meta.touched && meta.error}
                          onChange={handleChangeSubject.bind(null, input.onChange)}
                        />
                      )}
                    />
                  </FormItem>
                  <FormItem
                    label={<FormLabel>{translate('feedbackSidebar.content')}</FormLabel>}
                    className={styles['content']}
                  >
                    <Field
                      name="content"
                      initialValue={initialValues.content}
                      render={({ input, meta }): React.ReactNode => (
                        <Textarea
                          {...input}
                          hasError={meta.touched && meta.error}
                          onChange={handleChangedContent.bind(null, input.onChange)}
                        />
                      )}
                    />
                  </FormItem>
                  {/* IN-3432: disable sending a copy for now while the backend does not support it */}
                  {false && deal!.client.email && (
                    <Field
                      type="checkbox"
                      name="sendCopy"
                      render={({ input }): React.ReactNode => (
                        <Checkbox
                          className={styles['send-copy']}
                          {...input}
                          label={translate('feedbackSidebar.sendCopy', { email: deal!.client.email })}
                          onChange={handleChangeSendCopy.bind(null, input.onChange)}
                        />
                      )}
                    />
                  )}
                </Box>
              </OverflowingFlexBox>
              <Element.Footer padding={4} display="flex" justifyContent="flex-end">
                <Button level="secondary" type="button" marginRight={3} onClick={handleCloseFeedbackMenu}>
                  {translate('actions.cancel')}
                </Button>
                <ButtonWithTooltip
                  level="primary"
                  disabled={invalid}
                  label={!invalid && mode === MODES.PREVIEW ? translate('preview.actions') : undefined}
                  placement="left"
                >
                  {translate('actions.send')}
                </ButtonWithTooltip>
              </Element.Footer>
            </>
          )}
        </Element>
      )}
    />
  );
};

export default FeedbackMenu;
